import type { CreateAdminCompanyDto, UpdateAdminCompanyDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminCompanyInput,
  V2UpdateAdminCompanyInput
} from '@idc-forms/schemas/api-v2/admin-companies'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { companiesAdminV2Service } from '../services/companies-admin-v2.service'

/**
 * Mutaciones de empresas contra la API v2. Identifican por id público (`cmp_`)
 * y, al completar, invalidan todo el árbol `['companies']` (listado + summary +
 * lookup + detalle). El lookup v1 vive en el mismo árbol y también se invalida:
 * marcar una empresa como predeterminada cambia lo que preseleccionan los
 * formularios de sociedad, agencia, cliente y negociación.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateCompanyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminCompanyInput) =>
      await companiesAdminV2Service.create(accessToken, body as CreateAdminCompanyDto),
    onSuccess: () => {
      toast.success('Empresa creada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateCompanyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminCompanyInput }) =>
      await companiesAdminV2Service.update(accessToken, id, body as UpdateAdminCompanyDto),
    onSuccess: () => {
      toast.success('Empresa actualizada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteCompanyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await companiesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Empresa eliminada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
